import { z } from 'zod';
import { APP_ROLES } from './roles.js';
import { TASK_STATUSES, TASK_ACTIONS } from './workflow.js';
import { TASK_PRIORITIES } from './priority.js';
import { TASK_COMPLEXITIES } from './reference.js';
import { ANALYTICS_RANGES } from './analytics.js';
import { AUDIT_ACTIONS, NOTIFICATION_CHANNELS, NOTIFICATION_STATUSES } from './domain.js';
import { DEPARTMENTS } from './departments.js';
import { TICKET_STATUSES } from './ticket-workflow.js';
/**
 * Validation schemas shared by the API boundary and the web forms.
 * The server treats these as authoritative — every mutating route parses its
 * body through the matching schema before any business logic runs (§26).
 */
/** `z.enum` needs a mutable non-empty tuple; our constants are readonly. */
function enumOf(values) {
    return z.enum(values);
}
export const isoDateTime = z
    .string()
    .trim()
    .refine((value) => !Number.isNaN(Date.parse(value)), {
    message: 'Must be a valid ISO-8601 date-time',
})
    .transform((value) => new Date(value).toISOString());
export const uuid = z.string().trim().min(1).max(64);
export const taskReference = z.coerce.number().int().positive();
export const LIMITS = {
    titleMin: 3,
    titleMax: 180,
    descriptionMax: 20_000,
    commentMin: 1,
    commentMax: 10_000,
    reasonMin: 3,
    reasonMax: 2_000,
    tagMax: 32,
    tagsMax: 12,
    searchMax: 200,
    pageSizeMax: 100,
    estimatedHoursMax: 2_000,
    displayNameMax: 48,
};
const tagSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(LIMITS.tagMax)
    .regex(/^[a-z0-9][a-z0-9 _-]*$/, 'Tags may contain letters, numbers, spaces, hyphens and underscores');
export const tagsSchema = z.array(tagSchema).max(LIMITS.tagsMax).default([]);
/* ---------------------------------------------------------------------------
 * Tasks
 * ------------------------------------------------------------------------- */
export const createTaskSchema = z.object({
    title: z.string().trim().min(LIMITS.titleMin).max(LIMITS.titleMax),
    description: z.string().trim().max(LIMITS.descriptionMax).default(''),
    priority: enumOf(TASK_PRIORITIES).default('MEDIUM'),
    complexity: enumOf(TASK_COMPLEXITIES).nullish(),
    projectId: uuid.nullish(),
    categoryId: uuid.nullish(),
    deadline: isoDateTime.nullish(),
    estimatedHours: z.number().min(0).max(LIMITS.estimatedHoursMax).nullish(),
    tags: tagsSchema.optional(),
    /** Only honoured for actors holding `task:create:assign_other`. */
    assigneeId: uuid.nullish(),
});
export const updateTaskSchema = z
    .object({
    title: z.string().trim().min(LIMITS.titleMin).max(LIMITS.titleMax).optional(),
    description: z.string().trim().max(LIMITS.descriptionMax).optional(),
    priority: enumOf(TASK_PRIORITIES).optional(),
    complexity: enumOf(TASK_COMPLEXITIES).nullish(),
    projectId: uuid.nullish(),
    categoryId: uuid.nullish(),
    deadline: isoDateTime.nullish(),
    estimatedHours: z.number().min(0).max(LIMITS.estimatedHoursMax).nullish(),
    tags: z.array(tagSchema).max(LIMITS.tagsMax).optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be supplied',
});
export const assignTaskSchema = z.object({
    /** `null` unassigns the task. */
    assigneeId: uuid.nullable(),
    note: z.string().trim().max(LIMITS.reasonMax).nullish(),
});
export const transitionTaskSchema = z.object({
    action: enumOf(TASK_ACTIONS),
    comment: z.string().trim().max(LIMITS.commentMax).nullish(),
    /** Structured "what must change" text captured on REQUEST_CHANGES. */
    requiredChanges: z.string().trim().max(LIMITS.commentMax).nullish(),
});
export const deleteTaskSchema = z.object({
    reason: z.string().trim().min(LIMITS.reasonMin).max(LIMITS.reasonMax),
});
export const restoreTaskSchema = z.object({
    reason: z.string().trim().max(LIMITS.reasonMax).nullish(),
});
/* ---------------------------------------------------------------------------
 * Conversation
 * ------------------------------------------------------------------------- */
export const createCommentSchema = z.object({
    body: z.string().trim().min(LIMITS.commentMin).max(LIMITS.commentMax),
    parentId: uuid.nullish(),
});
export const updateCommentSchema = z.object({
    body: z.string().trim().min(LIMITS.commentMin).max(LIMITS.commentMax),
});
/* ---------------------------------------------------------------------------
 * Search, filtering, sorting (§16)
 * ------------------------------------------------------------------------- */
export const TASK_SORTS = [
    'NEWEST',
    'OLDEST',
    'PRIORITY',
    'DEADLINE',
    'TIME_IN_PROGRESS',
    'TIME_AWAITING_REVIEW',
    'DEVELOPER',
    'RECENTLY_UPDATED',
    'REFERENCE',
];
export const TASK_SORT_LABELS = {
    NEWEST: 'Newest first',
    OLDEST: 'Oldest first',
    PRIORITY: 'Priority',
    DEADLINE: 'Deadline',
    TIME_IN_PROGRESS: 'Time in progress',
    TIME_AWAITING_REVIEW: 'Time awaiting review',
    DEVELOPER: 'Developer',
    RECENTLY_UPDATED: 'Recently updated',
    REFERENCE: 'Task number',
};
export const TASK_VIEWS = ['MINE', 'TEAM', 'ALL', 'REVIEW_QUEUE'];
export const REVIEW_STATUSES = ['ANY', 'AWAITING_REVIEW', 'CHANGES_REQUESTED', 'APPROVED', 'NEVER_REVIEWED'];
/** Accepts `?status=NEW&status=CLAIMED` and `?status=NEW,CLAIMED`. */
function csvArray(values) {
    return z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((input) => {
        if (input === undefined)
            return undefined;
        const raw = Array.isArray(input) ? input : [input];
        const flat = raw.flatMap((item) => item.split(',')).map((item) => item.trim()).filter(Boolean);
        return flat.length > 0 ? flat : undefined;
    })
        .pipe(z.array(enumOf(values)).min(1).optional());
}
function csvStrings() {
    return z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((input) => {
        if (input === undefined)
            return undefined;
        const raw = Array.isArray(input) ? input : [input];
        const flat = raw.flatMap((item) => item.split(',')).map((item) => item.trim()).filter(Boolean);
        return flat.length > 0 ? flat : undefined;
    });
}
const booleanish = z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => {
    if (value === undefined)
        return undefined;
    if (typeof value === 'boolean')
        return value;
    return value === 'true' || value === '1' || value === 'yes';
});
export const taskQuerySchema = z.object({
    q: z.string().trim().max(LIMITS.searchMax).optional(),
    view: enumOf(TASK_VIEWS).default('ALL'),
    status: csvArray(TASK_STATUSES),
    priority: csvArray(TASK_PRIORITIES),
    assigneeId: csvStrings(),
    creatorId: csvStrings(),
    projectId: csvStrings(),
    categoryId: csvStrings(),
    tag: csvStrings(),
    reviewStatus: enumOf(REVIEW_STATUSES).default('ANY'),
    createdFrom: isoDateTime.optional(),
    createdTo: isoDateTime.optional(),
    updatedFrom: isoDateTime.optional(),
    updatedTo: isoDateTime.optional(),
    deadlineFrom: isoDateTime.optional(),
    deadlineTo: isoDateTime.optional(),
    completedFrom: isoDateTime.optional(),
    completedTo: isoDateTime.optional(),
    overdueOnly: booleanish,
    unassignedOnly: booleanish,
    includeDeleted: booleanish,
    includeArchived: booleanish,
    sort: enumOf(TASK_SORTS).default('RECENTLY_UPDATED'),
    direction: z.enum(['asc', 'desc']).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(25),
});
export const reviewQueueQuerySchema = z.object({
    q: z.string().trim().max(LIMITS.searchMax).optional(),
    priority: csvArray(TASK_PRIORITIES),
    assigneeId: csvStrings(),
    projectId: csvStrings(),
    sort: z.enum(['WAITING_LONGEST', 'WAITING_SHORTEST', 'PRIORITY', 'DEADLINE', 'DEVELOPER']).default('PRIORITY'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(25),
});
/* ---------------------------------------------------------------------------
 * Analytics
 * ------------------------------------------------------------------------- */
export const analyticsQuerySchema = z
    .object({
    range: enumOf(ANALYTICS_RANGES).default('LAST_30_DAYS'),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    projectId: uuid.optional(),
})
    .refine((value) => value.range !== 'CUSTOM' || (value.from && value.to), {
    message: 'A custom range requires both `from` and `to`',
    path: ['from'],
})
    .refine((value) => !value.from || !value.to || Date.parse(value.from) <= Date.parse(value.to), {
    message: '`from` must not be after `to`',
    path: ['from'],
});
/* ---------------------------------------------------------------------------
 * Administration
 * ------------------------------------------------------------------------- */
/**
 * A director-assigned display name shown everywhere in place of the raw
 * Discord identity. Control characters are stripped the same way an uploaded
 * filename is (see `sanitiseFilename`) — this text is embedded verbatim into
 * Discord DM and audit-channel content, so it gets the same defensive
 * treatment. An empty string is treated the same as omitting the field: use
 * `null` to explicitly clear an existing override back to the Discord name.
 */
export const displayNameOverride = z
    .string()
    .trim()
    .max(LIMITS.displayNameMax)
    .transform((value) => value.replace(/[\u0000-\u001F\u007F]/g, '').trim())
    .refine((value) => value.length > 0, { message: 'Must not be empty — use null to clear it instead' });
export const updateUserSchema = z
    .object({
    /** Explicit override of the Discord-derived role. `null` clears it. */
    roleOverride: enumOf(APP_ROLES).nullish(),
    isActive: z.boolean().optional(),
    /** `null` clears the override, reverting to the Discord-derived name. */
    displayNameOverride: displayNameOverride.nullish(),
    reason: z.string().trim().max(LIMITS.reasonMax).nullish(),
})
    .refine((value) => value.roleOverride !== undefined || value.isActive !== undefined || value.displayNameOverride !== undefined, { message: 'Nothing to update' });
export const roleMappingSchema = z.object({
    discordRoleId: z.string().trim().regex(/^\d{5,32}$/, 'Must be a Discord snowflake'),
    appRole: enumOf(APP_ROLES),
    label: z.string().trim().min(1).max(120),
});
export const referenceItemSchema = z.object({
    key: z
        .string()
        .trim()
        .toLowerCase()
        .min(1)
        .max(48)
        .regex(/^[a-z0-9][a-z0-9-]*$/, 'Use lowercase letters, numbers and hyphens'),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullish(),
    colour: z
        .string()
        .trim()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Must be a hex colour such as #4f7cff')
        .nullish(),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(9999).default(0),
});
export const updateSettingSchema = z.object({
    value: z.unknown(),
});
export const auditQuerySchema = z.object({
    action: csvArray(AUDIT_ACTIONS),
    actorId: csvStrings(),
    entityType: z.string().trim().max(64).optional(),
    entityId: z.string().trim().max(64).optional(),
    severity: csvArray(['INFO', 'NOTICE', 'WARNING', 'SECURITY']),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    q: z.string().trim().max(LIMITS.searchMax).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(50),
});
export const notificationQuerySchema = z.object({
    status: csvArray(NOTIFICATION_STATUSES),
    channel: csvArray(NOTIFICATION_CHANNELS),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(50),
});
/* ---------------------------------------------------------------------------
 * Auth
 * ------------------------------------------------------------------------- */
export const oauthCallbackSchema = z.object({
    code: z.string().trim().min(1).max(512).optional(),
    state: z.string().trim().min(1).max(512).optional(),
    error: z.string().trim().max(256).optional(),
    error_description: z.string().trim().max(512).optional(),
});
/* ---------------------------------------------------------------------------
 * Leadership Requests ("tickets")
 * ------------------------------------------------------------------------- */
export const createTicketSchema = z.object({
    title: z.string().trim().min(LIMITS.titleMin).max(LIMITS.titleMax),
    description: z.string().trim().max(LIMITS.descriptionMax).default(''),
    department: enumOf(DEPARTMENTS),
});
export const ticketReference = z.coerce.number().int().positive();
export const assignTicketSchema = z.object({
    assigneeId: uuid,
});
export const completeTicketSchema = z.object({
    comment: z.string().trim().max(LIMITS.commentMax).nullish(),
});
export const denyTicketSchema = z.object({
    reason: z.string().trim().min(LIMITS.reasonMin).max(LIMITS.reasonMax),
});
/** Creating a task from a ticket reuses `createTaskSchema` verbatim (the
 * brief is explicit: "same options as doing it through the main developer
 * interface") — no separate schema needed; the route just also requires an
 * `assigneeId`, since a ticket-spawned task is always handed to someone. */
export const createTicketTaskSchema = createTaskSchema.extend({
    assigneeId: uuid,
});
export const ticketQuerySchema = z.object({
    department: csvArray(DEPARTMENTS),
    status: csvArray(TICKET_STATUSES),
    /** Restricts to tickets the viewer personally opened or is assigned to. */
    mine: z
        .union([z.boolean(), z.string()])
        .optional()
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true')),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(25),
});
//# sourceMappingURL=schemas.js.map